import { Link } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';

const FAQS = [
  {
    q: 'What is Brotherhood+?',
    a: "Brotherhood+ is a men's emotional and mental wellness app. It brings daily emotional check-ins, guided and voice journaling, a supportive AI coach, practical lessons, a community, and a directory of culturally responsive therapists into one private place. It is created with men of color in mind and open to every man ready to grow.",
  },
  {
    q: 'Who is Brotherhood+ for?',
    a: 'It is built with the experiences of Black men and men of color at its center, and it is open to every man who wants to feel steadier, more connected, and more like himself. Brotherhood+ is intended for adults 18 and older.',
  },
  {
    q: 'Is Brotherhood+ therapy or a replacement for a therapist?',
    a: 'No. Brotherhood+ is a wellness and personal-growth platform that offers reflection, education, and support. The in-app AI coach is not a licensed therapist and is not a substitute for professional diagnosis or treatment. When you want clinical care, our free therapist directory helps you find a licensed professional.',
  },
  {
    q: 'Are my journal entries, check-ins, and coach conversations private?',
    a: 'Yes. Your journal entries, check-in responses, AI coach conversations, and account details are private to your account and kept behind secure sign-in. We do not sell your personal information and we do not share this content with advertisers or data brokers. See our Privacy Policy for details.',
  },
  {
    q: 'Is there a free trial?',
    a: 'Yes. Every new member starts with a 7-day free trial when you create your account, so you can experience Brotherhood+ before you pay anything.',
  },
  {
    q: 'How much does Brotherhood+ cost?',
    a: 'After your 7-day free trial, a Brotherhood+ membership is $14.99 per month or $99.99 per year (the annual plan is about two months free). You are billed only after the trial ends, and you can cancel anytime.',
  },
  {
    q: 'How do I find a therapist?',
    a: 'Use the Brotherhood+ therapist directory. It is free and needs no account — browse culturally responsive, licensed therapists and filter by state, specialty, insurance, and language.',
  },
  {
    q: 'Can I use Brotherhood+ on my phone?',
    a: 'Yes. Brotherhood+ works in your mobile browser and can be installed to your home screen, so your check-ins and journaling are always a tap away.',
  },
  {
    q: 'What should I do in a crisis?',
    a: 'If you or someone else is in immediate danger, call your local emergency number. Brotherhood+ also lists crisis and support hotlines on our crisis resources page. The AI coach is not an emergency service.',
  },
];

export default function FAQ() {
  const jsonLd = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: FAQS.map((f) => ({
      '@type': 'Question',
      name: f.q,
      acceptedAnswer: { '@type': 'Answer', text: f.a },
    })),
  };

  return (
    <div className="min-h-screen bg-[#F9F8F6]" data-testid="faq-page">
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(jsonLd) }} />
      <div className="max-w-3xl mx-auto px-6 py-10 lg:py-14">
        <div className="flex items-center justify-between mb-8">
          <Link to="/" className="inline-flex items-center gap-2 text-sm text-navy/60 hover:text-navy transition-smooth" data-testid="faq-back">
            <ArrowLeft size={14}/> Home
          </Link>
          <Link to="/login?mode=signin" className="inline-flex items-center rounded-full border border-navy/15 text-navy text-sm font-semibold px-5 py-2 hover:bg-navy hover:text-white transition-smooth" data-testid="faq-signin">
            Sign in
          </Link>
        </div>

        <header className="mb-10">
          <p className="text-[11px] uppercase tracking-[0.24em] text-gold font-semibold">FAQ</p>
          <h1 className="font-heading text-4xl sm:text-5xl mt-2 leading-tight text-navy">
            Frequently asked questions
          </h1>
          <p className="mt-4 text-lg text-navy/70 leading-relaxed">
            Everything you want to know about Brotherhood+ — what it is, who it's for, how your data stays private,
            and what it costs.
          </p>
        </header>

        <div className="space-y-4">
          {FAQS.map((f, i) => (
            <section key={i} className="rounded-3xl bg-white p-6 lg:p-7 card-soft" data-testid={`faq-item-${i}`}>
              <h2 className="font-heading text-xl text-navy">{f.q}</h2>
              <p className="mt-3 leading-relaxed text-navy/80">{f.a}</p>
            </section>
          ))}
        </div>

        <div className="mt-10 rounded-3xl bg-navy text-white p-8 text-center">
          <h2 className="font-heading text-2xl">Still have a question?</h2>
          <p className="mt-2 text-white/70">
            Email <a href="mailto:brotherhoodplusapp@gmail.com" className="underline hover:text-white" data-testid="faq-email">brotherhoodplusapp@gmail.com</a> and we'll help.
          </p>
          <Link to="/register" className="mt-5 inline-flex items-center justify-center rounded-full bg-gold text-navy px-7 py-3 text-sm font-bold hover:opacity-90 transition-smooth" data-testid="faq-cta">
            Start your 7-day free trial
          </Link>
        </div>

        <footer className="mt-12 pt-6 border-t border-navy/10 text-sm text-navy/55">
          <p className="flex flex-wrap gap-x-4 gap-y-1">
            <Link to="/features" className="underline hover:text-navy">Features</Link>
            <Link to="/about" className="underline hover:text-navy">About</Link>
            <Link to="/directory" className="underline hover:text-navy">Therapist directory</Link>
            <Link to="/privacy" className="underline hover:text-navy">Privacy</Link>
            <Link to="/terms" className="underline hover:text-navy">Terms</Link>
          </p>
        </footer>
      </div>
    </div>
  );
}
